import React, { useState, useRef } from "react";
import AsyncSelect from "react-select/async";
import axios from "axios";
import { signInUser } from "../../Utility/Auth";
import { useDispatch, useSelector } from "react-redux";
import { saleReturnProducts } from "../../../features/posSlice";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api/";

// Debounce helper to prevent keystroke hammering
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const PosSearchReturnProduct = ({ supplierProductsRef }) => {
  const posData = useSelector((state) => state.posReducer);
  const [selectedOption, setSelectedOption] = useState(null);
  const selectRef = useRef(null);
  const cancelTokenRef = useRef(null);

  const dispatch = useDispatch();
  const pnpMc = [54, 59, 61]; // Array to check for PNP products

  // Internal data fetching logic
  const fetchDataInternal = async (inputValue, callback) => {
    const auth = signInUser();
    const { aamarId, warehouse } = auth || {};

    if (!aamarId) {
      callback([]);
      return;
    }

    // Cancel the previous request if it exists
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Cancel The Previous Request");
    }

    // Create a new cancel token
    cancelTokenRef.current = axios.CancelToken.source();

    const articleCodeStr = (inputValue || "").toString().trim();
    const startsWithAny = pnpMc.some((item) =>
      articleCodeStr.startsWith(item.toString())
    );

    let parsedQty = 1;
    if (startsWithAny && articleCodeStr.length >= 12) {
      const wBefore = articleCodeStr.substring(7, 9);
      const wAfter = articleCodeStr.substring(9, 12);
      parsedQty = parseFloat(`${wBefore}.${wAfter}`) || 1;
    }

    try {
      const baseUrl = BASE_URL.endsWith("/") ? BASE_URL : `${BASE_URL}/`;
      const query = encodeURIComponent(articleCodeStr);
      const result = await axios.get(
        `${baseUrl}product/search/${aamarId}/${warehouse}/${query}`,
        { cancelToken: cancelTokenRef.current.token }
      );

      const tempArray = [];
      const products = Array.isArray(result?.data) ? result.data : [];

      if (products.length > 0) {
        products.forEach((element) => {
          const activePrices = Array.isArray(element?.priceList)
            ? element.priceList
            : [];
          const priceItem =
            activePrices.find((p) => p.status === "active") || activePrices[0];
          const mrp = priceItem?.mrp ?? 0;
          const priceId = priceItem?._id || "";

          tempArray.push({
            label: `${element?.name || ""} - [ ${element?.article_code || ""} ] ${mrp} BDT - Stock: ${element?.stock ?? 0} ${element?.unit || ""}`,
            value: element?._id,
            price: priceId,
            product: element,
            qty: parsedQty,
          });
        });

        // Exact barcode match auto-selection (e.g. barcode scanner)
        if (products.length === 1 && articleCodeStr) {
          const pro = products[0];
          const isExactBarcode =
            pro?.article_code === articleCodeStr ||
            pro?.ean === articleCodeStr ||
            (articleCodeStr.length >= 7 && startsWithAny);

          if (isExactBarcode) {
            const activePrices = Array.isArray(pro?.priceList)
              ? pro.priceList
              : [];
            const priceItem =
              activePrices.find((p) => p.status === "active") || activePrices[0];
            const proPrice = priceItem?._id || "";
            setSelectedOption(null);
            getValue(pro._id, proPrice, pro, parsedQty);
          }
        }
      } else {
        tempArray.push({
          label: `No product found`,
          value: `no-product`,
        });
      }

      callback(tempArray);
    } catch (error) {
      if (axios.isCancel(error)) {
        // Request canceled
      } else {
        console.error("Error fetching data:", error);
        callback([]);
      }
    }
  };

  // Debounced search trigger
  const debouncedFetchData = useRef(
    debounce((inputValue, callback) => {
      fetchDataInternal(inputValue, callback);
    }, 300)
  ).current;

  // GET VALUE OF SELECTED ITEM
  const getValue = async (data, price, fallbackProduct, itemQty = 1) => {
    if (!data || data === "no-product" || data === "please select") return;

    try {
      const baseUrl = BASE_URL.endsWith("/") ? BASE_URL : `${BASE_URL}/`;
      const result = await axios.get(`${baseUrl}product/${data}`);

      if (result?.data) {
        handleAddToggleProduct(result.data, price, itemQty);
      } else if (fallbackProduct) {
        handleAddToggleProduct(fallbackProduct, price, itemQty);
      }
    } catch (error) {
      console.error("Error getting product details:", error);
      if (fallbackProduct) {
        handleAddToggleProduct(fallbackProduct, price, itemQty);
      }
    }
  };

  // HANDLE SELECT
  const handleSearchChange = (selected) => {
    if (
      selected &&
      selected.value &&
      selected.value !== "no-product" &&
      selected.value !== "please select"
    ) {
      setSelectedOption(null);
      getValue(selected.value, selected.price, selected.product, selected.qty || 1);
    }
  };

  const handleAddToggleProduct = (product, selectedPriceId, addQty = 1) => {
    if (!product) return;

    const currentReturnProducts = posData?.returnProducts || [];

    const existingProduct = currentReturnProducts.find(
      (item) => item.article_code === product.article_code
    );
    const rest = currentReturnProducts.filter(
      (item) => item.article_code !== product.article_code
    );

    // Resolve active price
    const priceList = Array.isArray(product.priceList) ? product.priceList : [];
    const activePrice =
      (selectedPriceId && priceList.find((p) => p._id === selectedPriceId)) ||
      priceList.find((p) => p.status === "active") ||
      priceList[0] ||
      {};

    const mrp = activePrice?.mrp !== undefined ? Number(activePrice.mrp) : 0;
    const tp = activePrice?.tp !== undefined ? Number(activePrice.tp) : 0;
    const priceId = activePrice?._id || product.priceId || "";

    const qtyToAdd =
      typeof addQty === "string" ? parseFloat(addQty) || 1 : Number(addQty) || 1;

    let returnList = [];
    if (existingProduct) {
      // If already in return list, increment quantity
      returnList = [
        ...rest,
        {
          ...existingProduct,
          qty: Number(existingProduct.qty || 0) + qtyToAdd,
        },
      ];
    } else {
      // If not in return list, add as new item
      returnList = [
        ...rest,
        {
          ...product,
          id: product._id,
          article_code: product.article_code,
          photo: product.photo,
          name: product.name,
          ean: product.ean,
          max_qty: 10000,
          priceId: priceId,
          qty: qtyToAdd,
          mrp: mrp,
          tp: tp,
          order: currentReturnProducts.length + 1,
        },
      ];
    }

    dispatch(saleReturnProducts(returnList));
  };

  return (
    <AsyncSelect
      value={selectedOption}
      loadOptions={debouncedFetchData}
      placeholder="Product Search"
      onChange={handleSearchChange}
      defaultOptions={false}
      classNamePrefix="react-select"
      innerRef={selectRef}
      ref={supplierProductsRef}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      }}
    />
  );
};

export default PosSearchReturnProduct;
